
import { defineComponent, onMounted, PropType, reactive } from 'vue'

// @ui-kit
import NewSelect from '@/ui-kit/NewSelect/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'
import NewInput from '@/ui-kit/NewInput/index.vue'
import NewModal from '@/common/new-components/NewModal/index.vue'
import NewTable from '@/common/new-components/NewTable/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'

// @new-services
import tmsAPI from '@/new-services/tms.service'
import locationAPI from '@/new-services/location.service'

// @types
import { TtnDirectionInvoiceShort, TtnTMS } from '@/new-services/types/tms'
import { CityT } from '@/core/types/cities.types'

// @composable
import useNotifications from '@/common/composable/useNotifications'
import { orders } from '@/core/realisations/orders.realisation'
import { required } from '@vuelidate/validators'
import useVuelidate from '@vuelidate/core'

import tripIcon from '@/assets/icons/trip.svg'
import directionIcon from '@/assets/icons/direction.svg'
import invoicesIcon from '@/assets/icons/invoices.svg'

const INVOICES_TABLE_COLUMNS = [
    {
        key: 'invoiceNumber',
        name: 'Накладная',
    },
    {
        key: 'cubature',
        name: 'Кубатура',
    },
    {
        key: 'weight',
        name: 'Физ. вес',
    },
]

export default defineComponent({
    components: {
        NewSelect,
        NewButton,
        NewInput,
        NewModal,
        NewTable,
        tripIcon,
        directionIcon,
        invoicesIcon,
        Loader,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        item: {
            type: Object as PropType<TtnTMS>,
            required: true,
        },
    },
    emits: ['close'],
    setup(props, { emit }) {
        const notifications = useNotifications()

        const edit_state = reactive({
            is_loading: false,
            ttnInvoices: [] as TtnDirectionInvoiceShort[],
            is_failed: false,
            //
            id: null as number | null,
            //
            fromCityId: null as number | null,
            toCityId: null as number | null,
            tripNumber: null as number | null,
            ttnNumber: null as string | null,
            //
            invoiceNumber: null as number | null,
            is_invoice_loading: false,
        })

        const cities_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as CityT[],
        })

        const loadCities = async () => {
            try {
                cities_state.is_loading = true

                const response = await locationAPI.getCities()
                cities_state.list = response.data.data

                cities_state.is_failed = false
            } catch (error) {
                cities_state.is_failed = true
                console.error(error)
            } finally {
                cities_state.is_loading = false
            }
        }

        const deleteInvoice = (invoiceId: number) => {
            const ttnInvoice = edit_state.ttnInvoices.find((item) => item.invoiceId === invoiceId)
            if (!ttnInvoice) return

            edit_state.ttnInvoices.splice(edit_state.ttnInvoices.indexOf(ttnInvoice), 1)
        }

        const closeEdit = () => emit('close')

        onMounted(() => {
            loadCities()

            edit_state.id = props.item.id
            edit_state.tripNumber = props.item.tripNumber
            edit_state.ttnNumber = props.item.ttnNumber
            edit_state.fromCityId = props.item.fromCity ? props.item.fromCity.id : null
            edit_state.toCityId = props.item.toCity ? props.item.toCity.id : null

            loadTtn()
        })

        const loadTtn = async () => {
            if (!edit_state.id) return

            try {
                edit_state.is_loading = true

                const response = await tmsAPI.getTtn(edit_state.id)

                edit_state.ttnInvoices = response.data.data.invoicesByDirection.flatMap(
                    (e) => e.invoices
                )

                edit_state.is_failed = false
            } catch (error) {
                edit_state.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении детальной информации произошла ошибка',
                })
            } finally {
                edit_state.is_loading = false
            }
        }

        const InvoiceValidation = {
            invoiceNumber: { required },
        }
        const invoice_form$ = useVuelidate(InvoiceValidation, edit_state)

        const addInvoice = async () => {
            invoice_form$.value.$touch()
            if (invoice_form$.value.$invalid || !edit_state.invoiceNumber) return

            const newInvoiceNumber = edit_state.invoiceNumber.toString()

            if (edit_state.ttnInvoices.find((i) => i.invoiceNumber === newInvoiceNumber)) {
                notifications.notify({
                    type: 'danger',
                    title: `${edit_state.invoiceNumber} уже добавлена`,
                    description: `Накладная ${edit_state.invoiceNumber} уже добавлена`
                })
                
                return
            }

            try {
                edit_state.is_invoice_loading = true
                const invoiceResponse = await orders.getInvoiceByInvoiceNumber(
                    edit_state.invoiceNumber
                )

                if (invoiceResponse && invoiceResponse.data) {
                    edit_state.ttnInvoices.push({
                        invoiceId: invoiceResponse.data.id,
                        invoiceNumber: edit_state.invoiceNumber.toString(),
                        cubature: invoiceResponse.data.cubature,
                        weight: invoiceResponse.data.weight,
                    })

                    edit_state.invoiceNumber = null
                }
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При добавлении накладной произошла ошибка',
                })
            } finally {
                edit_state.is_invoice_loading = false
            }
        }

        const EditTtnValidation = {
            fromCityId: { required },
            toCityId: { required },
            tripNumber: { required },
        }
        const edit_form$ = useVuelidate(EditTtnValidation, edit_state)

        const editTtn = async () => {
            edit_form$.value.$touch()
            if (edit_form$.value.$invalid || !edit_state.id) return

            try {
                edit_state.is_loading = true

                const payload = {
                    fromCityId: edit_state.fromCityId as number,
                    toCityId: edit_state.toCityId as number,
                    tripNumber: edit_state.tripNumber as number,
                    invoiceIds: edit_state.ttnInvoices.map((item) => item.invoiceId),
                }

                await tmsAPI.editTtn(edit_state.id, payload)

                notifications.notify({ title: 'ТТН успешно обновлен', type: 'success' })
                closeEdit()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При редактировании произошла ошибка',
                })
            } finally {
                edit_state.is_loading = false
            }
        }

        return {
            edit_state,
            cities_state,
            edit_form$,
            invoice_form$,
            loadTtn,
            addInvoice,
            editTtn,
            closeEdit,
            deleteInvoice,
            loadCities,
            INVOICES_TABLE_COLUMNS,
        }
    },
})
